import { useEffect, useMemo, useState } from 'react';
import { type Campaign, type CampaignDTO, CampaignLifecycleType } from 'src/common/interfaces/campaigns';
import type { CampaignType } from 'src/features/admin/campaigns/campaigns.types';
import { useGetCampaignsQuery } from '../campaigns.slice';
import { readStoredCampaigns } from '../helpers/campaign-storage';

export type UseCurrentCampaignHookResult = {
  campaign: Campaign | null;
  shouldPromote: boolean;
  isLoading: boolean;
};

export const mapCampaignFromDTO = (dto: CampaignDTO): Campaign => ({
  name: dto.name,
  type: dto.type as CampaignType,
  lifecycleType: dto.lifecycleType,
  amount: dto.amount,
  utmCampaign: dto.utmCampaign,
  startTime: new Date(dto.startDate).getTime(),
  endTime: new Date(String(dto.endDate)).getTime() || undefined,
  sessionExpiration: dto.sessionExpiration,
  ...(dto.type ? { type: dto.type } : {}),
  isFlashSale: dto.isFlashSale,
  isFrench: dto.isFrench,
  applicableIf: dto.applicableIf,
  flashSale: dto.flashSale,
});

export const useActiveCampaign = (): UseCurrentCampaignHookResult => {
  const [timeIsValid, setTimeIsValid] = useState<boolean | null>(null);
  const { data: campaignsResponse, isLoading } = useGetCampaignsQuery();

  const currentCampaign = useMemo<Campaign | null>(() => {
    if (!campaignsResponse?.data) return null;

    const storedCampaignName = readStoredCampaigns().activeCampaign?.name;
    const activeCampaigns = campaignsResponse.data;

    const storedCampaign = activeCampaigns.find((campaign) => campaign.name === storedCampaignName);

    if (storedCampaign) return mapCampaignFromDTO(storedCampaign);

    const currentFlashSale = activeCampaigns.find(
      (campaign) => campaign.lifecycleType === CampaignLifecycleType.TIME_BASED,
    );

    return currentFlashSale ? mapCampaignFromDTO(currentFlashSale) : null;
  }, [campaignsResponse]);

  useEffect(() => {
    if (!currentCampaign) return;

    const checkValidity = () => {
      setTimeIsValid(!currentCampaign.endTime || currentCampaign.endTime > Date.now());
    };

    const intervalId = setInterval(checkValidity, 1000);
    checkValidity();

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(intervalId);
    };
  }, [currentCampaign]);

  return useMemo<UseCurrentCampaignHookResult>(() => {
    if (!currentCampaign) {
      return { campaign: null, shouldPromote: false, isLoading };
    }

    return { campaign: currentCampaign, shouldPromote: Boolean(timeIsValid), isLoading: timeIsValid === null };
  }, [currentCampaign, isLoading, timeIsValid]);
};
